import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import {
  LhChange,
  LhSingleOptionSelectEvent,
  PatternlibDropdownCustomEvent,
  PatternlibPaginationCustomEvent,
  PatternlibRadiobuttonItemCustomEvent,
} from "@liebherr/patternlib/dist/types/components";
import { PatternlibRadiobuttonItem } from "@liebherr/patternlib-angular";
import { TranslateService } from "@ngx-translate/core";
import { Product } from "src/app/models/product.model";
import { ProductArea } from "src/app/models/product-area.model";

import { ProductsService } from "../../services/products.service";

@Component({
  selector: "lh-product-finder",
  templateUrl: "./product-finder.component.html",
  styleUrls: ["./product-finder.component.scss"],
})
export class ProductFinderComponent implements OnInit {
  public readonly cloudinaryProductThumbnailBaseUrl =
    "https://res.cloudinary.com/liebherr/image/upload/t_profile_product/";

  productAreas: ProductArea[] | null = null;
  selectedProductAreaId: string | undefined;
  serialNumber: string | undefined;
  foundProducts: Product[] | undefined;
  private productsPerPage = 3;
  currentPage = 1;
  loadingProductAreas = false;
  loadingProducts = false;

  @Output()
  productSelected = new EventEmitter<Product>();

  @Output()
  searchError = new EventEmitter<HttpErrorResponse>();

  @ViewChild("thisForm") form!: NgForm;

  @ViewChildren("productRadiobuttonItems")
  productRadiobuttonItems!: QueryList<PatternlibRadiobuttonItem>;

  selectedProductId: string | undefined;

  constructor(
    private productsService: ProductsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const params = this.activatedRoute.snapshot.queryParams;
    this.serialNumber = params["serialNo"];
    const productAreaId = params["productArea"];
    if (productAreaId) {
      this.selectedProductAreaId = productAreaId;
    }
    const manyResultsExpected = params["many"] === "true";

    if (this.translateService.currentLang) {
      this.LoadProductAreas(manyResultsExpected);
    }
    this.translateService.onLangChange.subscribe(() => {
      this.LoadProductAreas(manyResultsExpected);
    });
  }

  private LoadProductAreas(manyResultsExpected: boolean) {
    this.loadingProductAreas = true;
    const tempProductAreaId = this.selectedProductAreaId;
    this.selectedProductAreaId = undefined;
    this.productAreas = null;
    this.productsService
      .getMyProductAreas()
      .then((items) => {
        this.productAreas = items;
        this.selectedProductAreaId = tempProductAreaId;
        this.loadingProductAreas = false;
        if (this.productAreas && manyResultsExpected && this.form.form.valid) {
          this.search();
        }
      })
      .catch((reason: HttpErrorResponse) => {
        return this.searchError.emit(reason);
      });
  }

  search() {
    if (!this.serialNumber || this.serialNumber.length < 3) {
      return;
    }
    this.loadingProducts = true;
    this.foundProducts = undefined;
    this.currentPage = 1;
    if (this.selectedProductAreaId && this.serialNumber) {
      this.productsService
        .searchProducts(this.selectedProductAreaId, this.serialNumber)
        .then((items) => {
          if (items?.length === 1) {
            this.foundProducts = undefined;
            this.setQueryParam("many", "false").then(() =>
              this.select(items[0]),
            );
          } else {
            this.foundProducts = items;
            this.setQueryParam("many", "true");
          }
          this.loadingProducts = false;
        });
    }
  }

  reset() {
    this.selectedProductAreaId = undefined;
    this.serialNumber = undefined;
    this.foundProducts = undefined;
  }

  select(product: Product) {
    this.productSelected.emit(product);
  }

  compareProductAreas(p1: ProductArea, p2: ProductArea) {
    return p1 && p2 ? p1.id === p2.id : false;
  }

  onProductAreaChanged(
    event: PatternlibDropdownCustomEvent<LhSingleOptionSelectEvent>,
  ) {
    if (event.detail.selected) {
      this.selectedProductAreaId = event.detail.selected.value.toString();
      this.setQueryParam("productArea", event.detail.selected.value.toString());
      this.foundProducts = undefined;
    }
  }

  onSerialNumberChanged() {
    this.setQueryParam("serialNo", this.serialNumber ?? "");
  }

  private setQueryParam(key: string, value: string): Promise<boolean> {
    const params: Params = {};
    params[key] = value;
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  onRadioButtonSelected(value: PatternlibRadiobuttonItemCustomEvent<string>) {
    this.selectedProductId = value.detail;
    this.productRadiobuttonItems.forEach((element) => {
      element.checked = element.value === this.selectedProductId;
    });
  }

  onSearchClick() {
    if (!this.foundProducts) return;
    const [selectedProduct] = this.foundProducts.filter(
      (x) => x.productId === this.selectedProductId,
    );
    this.select(selectedProduct);
  }

  get productsOnThisPage() {
    return this.foundProducts?.slice(
      (this.currentPage - 1) * this.productsPerPage,
      (this.currentPage - 1) * this.productsPerPage + 3,
    );
  }

  get lastPage(): number {
    return Math.ceil((this.foundProducts?.length ?? 0) / this.productsPerPage);
  }

  onPaginationChange(event: PatternlibPaginationCustomEvent<LhChange>) {
    this.currentPage = +event.detail.value;
    this.selectedProductId = undefined;
  }
}
